// Axios
import axios, { AxiosStatic } from 'axios';
import * as DataTable from 'datatables.net-dt';

// Laravel Echo
import Echo from 'laravel-echo';
import * as select2 from 'select2';

require('../initSentry');

// jQuery
const $: jQueryInterface = require('jquery');

$.DataTable = DataTable;
$.select2 = select2;

$.fn.extend({
    freshData(key, value) {
        // This function returns parsed fresh values of data-* attributes.
        // jQuery.data function caches every element's data-* attribute value
        // and thus it is unable to fetch updated values after changes in
        // the DOM have been made.
        // jQuery.attr on the other hand returns unparsed strings.
        if (typeof this === 'undefined' || !this.length) {
            return false;
        }

        const elem = this[0];
        if (typeof value !== 'undefined') {
            $(elem).attr(`data-${key}`, value);
        }

        try {
            return JSON.parse($(elem).attr(`data-${key}`));
        } catch (e) {
            return $(elem).attr(`data-${key}`);
        }
    },
});

window.$ = window.jQuery = $;

// Bootstrap
require('bootstrap-datepicker');
require('bootstrap');

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
window.axios = axios;

const token = document.head.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
if (token) {
    window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content;
}

import * as Sentry from '@sentry/browser';

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;
const retryCounts = new Map();
axios.interceptors.response.use(undefined, async (error) => {
    const config = error.config;

    if (!config || !config.url || axios.isCancel(error)) return Promise.reject(error);

    const retryKey = `${config.method ?? 'get'}|${config.url}`;
    const currentRetry = retryCounts.get(retryKey) || 0;

    if (currentRetry < MAX_RETRIES && (!error.response || error.code === 'ECONNABORTED')) {
        retryCounts.set(retryKey, currentRetry + 1);
        await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
        return axios(config);
    }

    retryCounts.delete(retryKey);

    if (error.response && ![401, 404, 422].includes(error.response.status)) {
        Sentry.captureException(error, {
            tags: {
                axios: 'global',
                method: config.method ?? 'unknown',
                url: config.url ?? 'unknown',
                retryCount: String(currentRetry),
            },
            extra: { config },
        });
    }

    return Promise.reject(error);
});

if (process.env.MIX_NOTIFICATIONS_ENABLED === 'true') {
    window.io = require('socket.io-client');

    if (typeof window.io !== 'undefined') {
        const port = process.env.MIX_NOTIFICATIONS_PORT ?? '6001';
        const portColon = port?.length ? ':' : '';

        window.Echo = new Echo({
            broadcaster: 'socket.io',
            host: `${window.location.hostname}${portColon}${port}`,
            encrypted: true,
        });
    }
}

// Globals
declare global {
    interface Window {
        $: jQueryInterface;
        jQuery: jQueryInterface;
        axios: AxiosStatic;
        Echo: Echo;
        io: object;
        dataLayer: any[];
        fbq: (...args: any[]) => void;
        srtmCommands: any[];
        SportRadarEvents: {};
        grecaptcha: any;
        handleReCaptchaChange: (captchaToken: string) => void;
    }

    interface jQueryInterface extends JQueryStatic {
        select2?: any;
        DataTable?: any;
        freshData: any;
    }

    interface JQuery {
        datepicker: (...args: any) => any;
        slick(...args: any): JQuery;
    }
}

$('.hide-before-load').removeClass('hide-before-load');

// Exports
export { axios, $ };
